export interface IAccountFilteredListParams {
  name?: string;
  page?: number;
  itemCount?: number;
}

export interface ICountry {
  polishName: string;
  englishName: string;
  codeAlfa2: string;
  codeAlfa3: string;
}

export interface IAddress {
  street: string;
  houseNumber: string;
  flatNumber: string;
  zipCode: string;
  city: string;
  country: ICountry;
}

export interface IEmail {
  value: string;
}

export interface IPhoneNumber {
  prefix: string;
  number: string;
}

export interface ICompanyId {
  companyIdentification: string;
  country: ICountry;
}

export interface ITaxOffice {
  name: string;
  address: IAddress;
}

export interface IAccountDetails {
  bankName: string;
  bankAccountNumber: string;
  swift: string;
}

export interface IAvailableStatementTemplate {
  templateName: string;
  available: boolean;
}
export interface IAvailableService {
  serviceId: number;
  serviceName: string;
  available: boolean;
}

export interface IAvailableSettlementMethod {
  settlementMethodId: number;
  settlementMethodName: string;
  available: boolean;
}

export interface ICurrency {
  currencyCode: string | null;
  name: string;
  id: number;
}

export interface IEndpoints {
  apiEndpoint: string;
  webEndpoint: string;
}

export interface IVersions {
  apiVersion: string;
  webVersion: string;
  lastCheckedAt: string | null;
}

export interface ILicenseAddress {
  street: string;
  houseNumber: string;
  flatNumber: string;
  zipCode: string;
  city: string;
  countryAlfa2: string;
}

export interface ILicenseTaxOffice {
  name: string;
  street: string;
  houseNumber: string;
  flatNumber: string;
  zipCode: string;
  city: string;
  countryAlfa2: string;
}

export interface ILicense {
  availableFrom: string;
  availableTo: string;
  firstName: string;
  lastName: string;
  fullName: string;
  name: string;
  email: string;
  phoneNumberPrefix: string;
  phoneNumber: string;
  address: ILicenseAddress;
  taxOffice: ILicenseTaxOffice;
  bankAccountNumber: string;
  bankName: string;
  swift: string;
  companyId: string;
  companyCountryAlfa2: string;
  availableServices: IAvailableService[];
  availableSettlementMethods: IAvailableSettlementMethod[];
  defaultCurrencyId: number;
  showOwnersLogoAsMain: boolean;
  deliveryCode: number;
  smartlinkDomains: IAccountFormSmartlinkDomain[];
  clientAccessModeId: number;
  isrcGenerationMode: number | null;
  clientModuleAccess: boolean;
  enableDigitalDelivery: boolean;
  implementationMode?: boolean;
  routingNumber?: string | null;
  availableStatementTemplateIds: number[];
  demoModeId: DEMO_MODE | null;
  isDetailedReportingEnabled?: boolean;
}

export interface IAccountResponseBase {
  id: string;
  accountName: string;
}

export interface IAccountResponse extends IAccountResponseBase, IVersions {}

export interface IAccountDetailResponse extends IAccountResponseBase {
  versions: IVersions | null;
  apiKey: string;
  license: ILicense | null;
  version: number;
  remarks?: string;
  endpoint: string;
  clientEndpoint: string;
}

export interface ICreateAccount {
  accountName: string;
  endpoint: string;
  clientEndpoint: string;
  apiKey: string;
  license: IAccountFormLicense | null;
}

export interface IUpdateAccount {
  magellanAccountId: string;
  accountName: string;
  apiEndpoint: string;
  clientEndpoint: string;
  apiKey: string;
}

export interface IAccountPagesResponse {
  items: IAccountResponse[];
  totalCount: number;
  totalPageCount: number;
}

export interface IAccountMutationForm {
  id: string;
  withLicense?: boolean;
  accountName: string;
  endpoint: string;
  clientEndpoint: string;
  apiKey: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  fullName: string;
  phoneNumberPrefix: string;
  phoneNumber: string;
  availableFrom: string;
  availableTo: string;
  withEndDate: boolean;
  defaultCurrencyId: number;
  deliveryCode: number;
  clientAccessModeId: number;
  isrcGenerationMode: number;
  showOwnersLogoAsMain: boolean;
  clientModuleAccess: boolean;
  enableDigitalDelivery: boolean;
  availableSettlementMethods: IAvailableSettlementMethod[];
  availableStatementTemplates: IAvailableStatementTemplate[];
  availableStatementTemplateIds: number[];
  availableServices: IAvailableService[];
  smartlinkDomains: IAccountFormSmartlinkDomain[];
  address: {
    street: string;
    houseNumber: string;
    flatNumber: string;
    zipCode: string;
    city: string;
    countryAlfa2: string;
  };
  taxOffice: {
    name: string;
    street: string;
    houseNumber: string;
    flatNumber: string;
    zipCode: string;
    city: string;
    countryAlfa2: string;
  };
  companyId: string;
  companyCountryAlfa2: string;
  bankAccountNumber: string;
  bankName: string;
  swift: string;
  isDemo: boolean | null;
  demoModeId: DEMO_MODE | null;
}

export interface IAccountFormAddress {
  street: string;
  houseNumber: string;
  flatNumber: string;
  zipCode: string;
  city: string;
  countryAlfa2: string;
}

export interface IAccountFormTaxOffice {
  name: string;
  street: string;
  houseNumber: string;
  flatNumber: string;
  zipCode: string;
  city: string;
  countryAlfa2: string;
}

export interface IAccountFormSmartlinkDomain {
  id: string;
  name: string;
  boardId?: string;
  boardName?: string;
}

export interface IAccountFormLicense {
  availableFrom: string;
  availableTo: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  name: string;
  email: string;
  phoneNumberPrefix: string;
  phoneNumber: string;
  address: IAccountFormAddress;
  taxOffice: IAccountFormTaxOffice;
  bankAccountNumber: string;
  bankName: string;
  swift: string;
  companyId: string;
  companyCountryAlfa2: string;
  availableServices: IAvailableService[];
  availableSettlementMethods: IAvailableSettlementMethod[];
  availableStatementTemplates: IAvailableStatementTemplate[];
  defaultCurrencyId: number;
  showOwnersLogoAsMain: boolean;
  deliveryCode: number;
  smartlinkDomains: IAccountFormSmartlinkDomain[];
  clientAccessModeId: number;
  isrcGenerationMode: number;
  clientModuleAccess: boolean;
  enableDigitalDelivery: boolean;
  isDemo: boolean;
  demoModeId: DEMO_MODE | null;
}

export interface IAccountLicenceMutation {
  magellanAccountId: string;
  license: IAccountFormLicense | null;
}

export enum ISRC_GENERATION_MODE {
  generative = 1,
  pool = 2,
}

export enum DEMO_MODE {
  client = 1,
  presentation = 2,
}
